import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { ApiDataSource } from '../../../utils/api-data-source';
import { ApiRequestService } from '../../../shared/api-request.service';
import { MatSort, Sort } from '@angular/material/sort';
import { EmployeesSelectorComponent } from '../../../employees/employees-selector/employees-selector.component';
import { UserPublicProfileComponent } from '../../../shared/user-public-profile/user-public-profile.component';
import { MatPaginator } from '@angular/material/paginator';
import { RBACService } from '../../../shared/rbac.service';

@Component({
  selector: 'app-rbac-roles-users',
  templateUrl: './rbac-roles-users.component.html',
  styleUrls: ['./rbac-roles-users.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class RbacRolesUsersComponent implements OnInit, AfterViewInit {
  // Get the RBAC role id.
  @Input('rbac_role_id') rbac_role_id: number;

  // Columns to show and the data source.
  displayedColumns: string[] = [];
  dataSource: RbacRoleUsersDataSource = new RbacRoleUsersDataSource(
    this.app,
    this.api,
    true
  );

  // RBAC permission flag. Resolved once in ngOnInit() so the template does not
  // re-evaluate the permission check on every change detection cycle.
  //
  // Attaching and detaching members are both membership edits, so a single flag covers them.
  // Mirrors RbacRolesUsersController@update and @destroy, which both require rbac-roles:edit.
  canManageMembers: boolean = false;

  // The paginator and sorter.
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public rbac: RBACService
  ) {}

  ngOnInit(): void {
    // Resolve the RBAC permission flag used by the template.
    this.canManageMembers = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_RBAC_ROLES,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );

    // Store the RBAC role id before making API requests.
    this.dataSource.rbac_role_id = this.rbac_role_id;

    // Build displayedColumns dynamically based on permissions
    this.displayedColumns = ['contact_person', 'email', 'mobile', 'trade'];

    // The select column drives bulk unlink, and the actions column holds the per-row Unlink
    // button, so both depend on being able to change the role's membership.
    if (this.canManageMembers) {
      this.displayedColumns.unshift('select');
      this.displayedColumns.push('actions');
    }

    // Make the initial API request to get the list of users.
    this.dataSource.getData(true);
  }

  ngAfterViewInit(): void {
    // Reset the paginator when sorting.
    this.sort.sortChange.subscribe(
      (): void => {
        this.paginator.pageIndex = 0;
      }
    );

    const _tap: any = tap(
      (): void => {
        this.dataSource.limit = this.paginator.pageSize;
        this.dataSource.offset = this.paginator.pageIndex;
        this.dataSource.order_by = this.sort.active;
        this.dataSource.order = this.sort.direction;

        // Refresh the data.
        this.dataSource.getData();
      }
    );

    // Subscribe to the paginator tap events.
    this.paginator.page.pipe(_tap).subscribe();

    // Subscribe to the sorter tap events.
    this.sort.sortChange
      .pipe(
        tap(
          (sorter: Sort): void => {
            this.dataSource.order = sorter['direction'];
            this.dataSource.getData(true);
          }
        )
      )
      .subscribe();
  }

  /**
   * Whether this role is a partner role whose members are read-only to the current user.
   *
   * @returns {boolean}
   */
  get roleLocked(): boolean {
    return this.rbac.partnerLockApplies(!!this.dataSource.lastResponse?.isPartnerRole);
  }

  /**
   * Whether a member row is a partner user whose membership is read-only to the current user.
   *
   * @param row The member row.
   * @returns {boolean}
   */
  rowLocked(row: any): boolean {
    return this.rbac.partnerLockApplies(!!row?.is_partner_managed);
  }

  /**
   * Every partner-managed member of the role (unfiltered by search) when the lock applies, else none.
   *
   * @returns {number[]}
   */
  lockedMemberIds(): number[] {
    if (!this.rbac.partnerLockApplies(true)) {
      return [];
    }
    return this.dataSource.lastResponse?.partnerManagedIds ?? [];
  }

  /**
   * Opens the user selector dialog and syncs selected users with the RBAC role.
   *
   * @returns {void}
   */
  onSelectUsers(): void {
    if (!this.canManageMembers) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to manage users for RBAC roles.'
      );
      return;
    }

    // Partner role members are managed by support.
    if (this.roleLocked) {
      this.rbac.showGenericAccessDeniedMessage(this.rbac.partnerManagedMessage());
      return;
    }

    // Show the user selector.
    this.utils.showComponentDialog(
      EmployeesSelectorComponent,
      {
        multiple: true,
        selected: this.dataSource.allIds,
        // Partner users are shown locked and cannot be picked or dropped (ui-spec 4.3).
        lockPartnerManaged: true
      },
      {},
      (user_ids: number[]): void => {
        if (typeof user_ids !== 'undefined') {
          // Partner members always stay in the synced set, so the sync never removes them. Any partner
          // addition that still gets through is skipped by the API and named in the toast below.
          const lockedIds: number[] = this.lockedMemberIds();
          user_ids = [
            ...user_ids,
            ...lockedIds.filter((id: number): boolean => !user_ids.includes(id))
          ];

          // Make an update request to the API.
          this.api
            .makeRequest(
              'put',
              `v2/account/rbac-roles/${this.rbac_role_id}/users`,
              {
                user_ids: user_ids
              }
            )
            .then(
              (response: any): void => {
                this.utils.showToast(
                  'The selected ' +
                    this.utils
                      .getLangTerm('employees.plural', 'Employees')
                      .toLowerCase() +
                    ' were successfully synced to the RBAC role.' +
                    this.rbac.partnerSkippedNotice(response?.skipped)
                );
              }
            )
            .catch(
              (_error: any): void => {
                this.utils.handleAPIErrors(_error);
              }
            )
            .finally(
              (): void => {
                this.dataSource.getData(true);
              }
            );
        }
      }
    );
  }

  /**
   * Unlinks a user from the RBAC role.
   *
   * @param {number} user_id - The ID of the user to unlink.
   *
   * @param evt
   * @return {void}
   */
  onUnlinkUser(user_id: number, evt: any): void {
    if (!this.canManageMembers) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to unlink users from RBAC roles.'
      );
      return;
    }

    // Partner memberships are managed by support.
    if (this.roleLocked || this.lockedMemberIds().includes(user_id)) {
      this.rbac.showGenericAccessDeniedMessage(this.rbac.partnerManagedMessage());
      return;
    }

    this.utils.showQuickActions(
      evt.target,
      'Are you sure you want to unlink the ' +
        this.utils.getLangTerm('employees.singular', 'Employee').toLowerCase() +
        ' from the RBAC role?',
      [
        {
          text: 'Yes',
          handler: (): void => {
            // Unlink the user.
            this.unlinkUsers([user_id]);
          }
        },
        {
          text: 'No',
          handler: (): void => {}
        }
      ]
    );
  }

  /**
   * Unlinks selected users from the RBAC role.
   *
   * @return {void}
   */
  onUnlinkSelectedUsers(): void {
    if (!this.canManageMembers) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to unlink users from RBAC roles.'
      );
      return;
    }

    // Get the selected user ids.
    const ids: any[] = this.dataSource.selection.selected;
    // Check if we have users selected.
    if (ids.length == 0) {
      this.utils.showModal(
        'Unlink Users',
        'Please select some ' +
          this.utils
            .getLangTerm('employees.plural', 'Employees')
            .toLowerCase() +
          ' to unlink from the RBAC role.'
      );
      return;
    }

    // Partner memberships are managed by support. They are left out of the unlink (a select-all can
    // include them); only a selection of partner users alone is refused.
    const lockedIds: number[] = this.lockedMemberIds();
    const unlinkIds: number[] = ids.filter((id: number): boolean => !lockedIds.includes(id));
    if (this.roleLocked || unlinkIds.length === 0) {
      this.rbac.showGenericAccessDeniedMessage(this.rbac.partnerManagedMessage());
      return;
    }
    const keptCount: number = ids.length - unlinkIds.length;

    // Make a request to unlink the users from the RBAC role.
    this.utils.showModal(
      'Unlink Users',
      `Are you sure you want to unlink (${unlinkIds.length}) ${this.utils
        .getLangTerm('employees.plural', 'Employees')
        .toLowerCase()} from the RBAC role?` +
        (keptCount > 0
          ? ` ${keptCount} partner-managed ${this.utils
              .getLangTerm('employees.singular', 'Employee')
              .toLowerCase()}(s) in the selection will stay linked because their access is managed by ${this.utils.getAppName()}.`
          : ''),
      (): void => {
        this.unlinkUsers(unlinkIds).then(
          (): void => {
            // Clear the list selection.
            this.dataSource.selection.clear();
          }
        );
      }
    );
  }

  /**
   * This method unlinks the specified users from the RBAC role.
   *
   * @param {number[]} user_ids - An array of user IDs to unlink from the RBAC role.
   * @returns {Promise<any>} - A promise that resolves when the unlinking is complete.
   * @private
   */
  private unlinkUsers(user_ids: number[]): Promise<any> {
    // Make an API request to unlink users.
    return this.api
      .makeRequest(
        'delete',
        `v2/account/rbac-roles/${this.rbac_role_id}/users`,
        {},
        {
          user_ids: user_ids.join(',')
        }
      )
      .then(
        (response: any): void => {
          // The API answers 200 with "skipped" when it left partner users attached (api-spec 7.2a).
          this.utils.showToast(
            'The ' +
              this.utils
                .getLangTerm('employees.plural', 'Employees')
                .toLowerCase() +
              ' were detached from the RBAC role.' +
              this.rbac.partnerSkippedNotice(response?.skipped)
          );
          this.dataSource.getData();
        }
      )
      .catch(
        (_error: any): void => {
          this.utils.handleAPIErrors(_error);
          // Refresh so the UI reflects the unchanged server state.
          this.dataSource.getData();
        }
      );
  }

  /**
   *
   * @param {string} hash - The unique identifier (hash) of the user's public profile to open
   * @param {Event} event - The event object triggering the method
   * @return {void}
   */
  onOpenUserPublicProfileView(hash: string, event: Event): void {
    // Prevent the default events and propagation from triggering.
    event.preventDefault();
    event.stopPropagation();
    // Open the user's public profile view.
    this.utils.showComponentDialog(UserPublicProfileComponent, hash, {
      width: '90%'
    });
  }

  protected readonly RBACService = RBACService;
}

export class RbacRoleUsersDataSource extends ApiDataSource {
  // Order and order by params.
  order_by: string = 'contact_person';
  order: string = 'asc';

  // Store the RBAC role id.
  rbac_role_id: number;

  getData(resetOffset: boolean = false): void {
    this.makeRequest(
      `v2/account/rbac-roles/${this.rbac_role_id}/users`,
      resetOffset,
      {
        order_by: this.order_by,
        order: this.order
      }
    );
  }
}
